import type { WidgetConfig, WidgetState } from '../types';

/** Generates a stable per-chat-instance id (new chat, new tab -> different ids). */
export function generateClientChatId(): string {
  return Math.random().toString(36).substring(2) + Date.now().toString(36);
}

const DRIVING_MODE_STORAGE_KEY = 'mcp-chat-widget:driving-mode-active';
const DRIVING_AUDIO_OUTPUT_STORAGE_KEY = 'mcp-chat-widget:driving-audio-output-device-id';
const DRIVING_CUE_SOUNDS_STORAGE_KEY = 'mcp-chat-widget:driving-cue-sounds-enabled';

export function readPersistedDrivingModeActive(): boolean {
  if (typeof window === 'undefined') return false;
  try {
    return window.localStorage.getItem(DRIVING_MODE_STORAGE_KEY) === 'true';
  } catch {
    return false;
  }
}

export function persistDrivingModeActive(active: boolean): void {
  if (typeof window === 'undefined') return;
  try {
    window.localStorage.setItem(DRIVING_MODE_STORAGE_KEY, String(active));
  } catch { /* ignore */ }
}

export function readPersistedAudioOutputDeviceId(): string | undefined {
  if (typeof window === 'undefined') return undefined;
  try {
    const value = window.localStorage.getItem(DRIVING_AUDIO_OUTPUT_STORAGE_KEY);
    return value || undefined;
  } catch {
    return undefined;
  }
}

export function persistAudioOutputDeviceId(deviceId: string | undefined): void {
  if (typeof window === 'undefined') return;
  try {
    if (deviceId) {
      window.localStorage.setItem(DRIVING_AUDIO_OUTPUT_STORAGE_KEY, deviceId);
    } else {
      window.localStorage.removeItem(DRIVING_AUDIO_OUTPUT_STORAGE_KEY);
    }
  } catch { /* ignore */ }
}

export function readPersistedCueSoundsEnabled(): boolean {
  if (typeof window === 'undefined') return true;
  try {
    const stored = window.localStorage.getItem(DRIVING_CUE_SOUNDS_STORAGE_KEY);
    return stored === null ? true : stored === 'true';
  } catch {
    return true;
  }
}

export function persistCueSoundsEnabled(enabled: boolean): void {
  if (typeof window === 'undefined') return;
  try {
    window.localStorage.setItem(DRIVING_CUE_SOUNDS_STORAGE_KEY, String(enabled));
  } catch { /* ignore */ }
}

export const DEFAULT_CONFIG: WidgetConfig = {
  serverUrl: '',
  primaryColor: '#222035',
  position: 'bottom-right',
  title: '',
  greeting: '',
  otpGreeting: '',
  providerGreetings: {},
  language: 'he',
  auth: 'required',
  authProvider: 'none',
  uploadEnabled: true,
  sessionTimeout: 30,
  displayMode: 'floating',
  widgetId: null,
  allowedTools: undefined,
  dangerousTools: undefined,
  voiceEnabled: false,
  drivingModeEnabled: false,
  chatDebugUsageEnabled: false,
};

export const INITIAL_STATE: WidgetState = {
  config: { ...DEFAULT_CONFIG },
  isOpen: false,
  isExpanded: false,
  auth: { status: 'unauthenticated', email: null, token: null },
  messages: [],
  historyMessages: [],
  isLoading: false,
  error: null,
  fileUpload: { file: null, uploading: false, progress: 0, error: null },
  pendingAttachments: [],
  conversationId: null,
  clientChatId: '',
  boundTurnId: null,
  mcpSessionId: null,
  tools: [],
  toolCalls: [],
  pendingConfirmation: null,
  rateLimitLock: null,
  widgetInactive: false,
  drivingMode: {
    active: readPersistedDrivingModeActive(),
    listening: false,
    speaking: false,
    processing: false,
    transcript: '',
    lastResponse: '',
    error: null,
    keypadOpen: false,
    speechRate: 1,
    audioOutputDeviceId: readPersistedAudioOutputDeviceId(),
    drivingCueSoundsEnabled: readPersistedCueSoundsEnabled(),
  },
};
